import Button from '@atoms/Button/Button';
import { ILink } from '@models/ILink';
import { IVideo } from '@models/IVideo';
import InViewVideo from '@molecules/InViewVideo/InViewVideo';
import updateDataLayer from '@utils/helpers/gtm';
import usePreviewMode from '@utils/hooks/usePreviewMode';
import classNames from 'classnames';

interface Props {
  readonly video: IVideo;
  readonly title: string;
  readonly subTitle: string;
  readonly text?: string;
  readonly cta: ILink;
}

const VideoWithTextOver = ({ video, title, subTitle, text, cta }: Props) => {
  const isPreview = usePreviewMode();

  return (
    <section
      className="component-padding bg-black-100"
      data-testid="CT-152-Video-With-Text-Over"
    >
      <div
        className={classNames(
          'relative',
          isPreview ? 'h-[1080px]' : 'h-screen'
        )}
      >
        <div
          className="absolute inset-0 z-[1] bg-black bg-opacity-40"
          data-testid="CT-152-Video-With-Text-Over-Tint"
        />

        <div
          className="absolute top-0 z-[1] h-1/4 w-full bg-gradient-to-b from-black/100"
          data-testid="CT-152-Video-With-Text-Over-Top-Gradient"
        />

        <div
          className="absolute bottom-0 z-[1] h-1/4 w-full bg-gradient-to-t from-black/100"
          data-testid="CT-152-Video-With-Text-Over-Bottom-Gradient"
        />

        <div className="absolute inset-0 z-[1] flex items-center justify-center text-white">
          <div className="w-full text-center max-md:px-5 md:w-1/2 xl:w-1/3">
            <p data-testid="CT-152-Video-With-Text-Over-Title" className="h3">
              {title}
            </p>

            <p
              className="mount mb-7.5 lg:mb-10"
              data-testid="CT-152-Video-With-Text-Over-Subtitle"
            >
              {subTitle}
            </p>

            {text && (
              <p
                className="h6 mb-5 lg:mb-10"
                data-testid="CT-152-Video-With-Text-Over-Text"
              >
                {text}
              </p>
            )}

            {cta?.url && cta.name && (
              <Button
                link={cta}
                dataTestId="CT-152-Video-With-Text-Over-CTA"
                linkCallback={() => {
                  updateDataLayer({
                    event: 'selectContent',
                    content_type: 'video_cta',
                    link_text: cta.name,
                  });
                }}
              />
            )}
          </div>
        </div>

        <InViewVideo
          wrapperClasses="h-full"
          videoClasses="[&>div>video]:object-cover h-full"
          autoPlay
          controls={false}
          video={video}
          inViewThreshold={0}
          hasInVideoPlayButton
          inVideoPlayButtonClassName="z-10"
          dataTestId="CT-152-Video-With-Text-Over-Video-Container"
        />
      </div>
    </section>
  );
};

export default VideoWithTextOver;
